import * as React from 'react';
import { Badge, Box, Button, Collapse, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { CaretDown, CaretRight, Sparkle, Warning } from 'phosphor-react';
import { useQueryClient } from '@tanstack/react-query';

import { GlassCard } from '../../../../../../components/glass';
import {
  IPocOption,
  useContaCilPocs,
  useContaCilUpdateLancamento,
} from '../../../../../../hooks/useContaCilLancamentos';
import {
  IResiduoCluster,
  IResiduoLancamento,
  useContaCilCriarRegraDoCluster,
  useContaCilResiduoClusters,
} from '../../../../../../hooks/useContaCilSchoolAi';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import {
  POC_CONTROL_C,
  POC_CONTROL_D,
  PocSelectOption,
  fmtData,
  fmtValor,
  pocSelectStyles,
} from '../../conciliar/shared';
import { propsMenuSelect } from '../../../../../../utils/selectMenu';

const C = {
  violet: '#8B5CF6',
  amber: '#F59E0B',
  green: '#10B981',
  red: '#EF4444',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
};
const fmtNum = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

function ItemLinha({ lanc }: { lanc: IResiduoLancamento }) {
  const isEntrada = lanc.dc === 'C';
  return (
    <Flex align="center" gap={2} py="5px" px={2} fontSize="12px" borderRadius="md">
      <Text color={C.subtle} flexShrink={0} w="62px">
        {fmtData(lanc.data)}
      </Text>
      <Text color={C.ink} flex={1} minW={0} noOfLines={1}>
        {lanc.historico}
      </Text>
      <Text fontWeight="600" color={isEntrada ? C.green : C.red} flexShrink={0} whiteSpace="nowrap">
        {isEntrada ? '+' : '−'} {fmtValor(lanc.valor)}
      </Text>
    </Flex>
  );
}

interface ClusterCardProps {
  cluster: IResiduoCluster;
  pocOptions: PocSelectOption[];
  maxQtd: number;
  onApplied: () => void;
  empresaId: number;
}

function ClusterCard({ cluster, pocOptions, maxQtd, onApplied, empresaId }: ClusterCardProps) {
  const toast = useToast();
  const { mutateAsync } = useContaCilUpdateLancamento();
  const criarRegra = useContaCilCriarRegraDoCluster();
  const [open, setOpen] = React.useState(false);
  const [poc, setPoc] = React.useState<PocSelectOption | null>(null);
  const [aplicando, setAplicando] = React.useState(false);

  const isEntrada = cluster.dc === 'C';
  const contraField: 'debito_poc' | 'credito_poc' = isEntrada ? 'credito_poc' : 'debito_poc';
  // Dispersão alta = valores espalhados (mesma contraparte pode ir a contas
  // diferentes): exige expandir e revisar antes de aplicar ao grupo todo.
  const travadoPorDispersao = cluster.dispersao === 'alta' && !open;

  const usarMemoria = () => {
    if (!cluster.memoria) return;
    const opt = pocOptions.find(o => o.value === cluster.memoria?.poc_codigo);
    setPoc(opt ?? { value: cluster.memoria.poc_codigo, label: cluster.memoria.poc_codigo });
    setOpen(true);
  };

  const aplicar = async () => {
    if (!poc || aplicando) return;
    setAplicando(true);
    try {
      // PATCHs em série (mesma contraparte -> mesma linha de memória; em
      // paralelo correriam no upsert). Cada um já alimenta a memória L2.
      await cluster.itens.reduce(async (prev, item) => {
        await prev;
        await mutateAsync({ id: item.id, [contraField]: poc.value });
      }, Promise.resolve());
      toast({
        status: 'success',
        title: `${cluster.qtd} conciliados`,
        description: `A IA passa a reconhecer "${cluster.contraparte_exibe}" sozinha.`,
        duration: 5000,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao aplicar ao grupo',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    } finally {
      setAplicando(false);
      // Refetch sempre — mesmo em erro parcial (alguns PATCHs do lote podem ter
      // aplicado), pra lista refletir o estado real e não mostrar stale.
      onApplied();
    }
  };

  // Sprint 2 — vira o cluster numa regra permanente. Diferente de "Aplicar":
  // o aplicar resolve OS LANCAMENTOS de agora. Esta cria a REGRA que cobre
  // OS PROXIMOS — vc nao precisa repetir o trabalho mes que vem.
  const virarRegra = async () => {
    if (criarRegra.isPending) return; // guarda anti duplo-clique (isLoading só desabilita após o re-render)
    const pocPick = poc?.value ?? cluster.memoria?.poc_codigo ?? null;
    if (!pocPick) {
      toast({
        status: 'warning',
        title: 'Sem POC',
        description: 'Escolha uma conta primeiro (ou use a sugerida pela memória).',
      });
      return;
    }
    // Categoria dominante dos itens do cluster — mandar 'Outros' fixo poluía
    // relatórios (a regra nascia sem categoria real).
    const contagem = new Map<string, number>();
    cluster.itens.forEach(i => {
      if (i.categoria) contagem.set(i.categoria, (contagem.get(i.categoria) ?? 0) + 1);
    });
    const categoriaDominante =
      Array.from(contagem.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Outros';
    try {
      await criarRegra.mutateAsync({
        empresa_id: empresaId,
        contraparte_norm: cluster.chave_norm,
        categoria: categoriaDominante,
        dc: cluster.dc,
        kw: [cluster.chave_norm.split(' ').slice(0, 3).join(' ')],
        poc_codigo: pocPick,
      });
      toast({
        status: 'success',
        title: 'Regra criada',
        description: `Próximos lançamentos de "${cluster.contraparte_exibe}" vão conciliar automaticamente.`,
        duration: 5000,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao criar regra',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  const barPct = maxQtd > 0 ? Math.max(8, Math.round((cluster.qtd / maxQtd) * 100)) : 0;

  return (
    <GlassCard intensity="medium" p={0} overflow="hidden">
      <Box p={3.5}>
        <Flex align="flex-start" gap={3}>
          <Box
            as="button"
            onClick={() => setOpen(o => !o)}
            mt="2px"
            color={C.subtle}
            flexShrink={0}
            aria-label={open ? 'Recolher' : 'Expandir'}
          >
            {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
          </Box>
          <Box flex={1} minW={0}>
            <Flex align="center" gap={2} wrap="wrap">
              <Text fontSize="15px" fontWeight="700" color={C.violet} noOfLines={1}>
                {cluster.contraparte_exibe}
              </Text>
              <Badge
                borderRadius="full"
                colorScheme={isEntrada ? 'green' : 'red'}
                fontSize="9px"
                px={2}
              >
                {isEntrada ? 'entrada' : 'saída'}
              </Badge>
              {cluster.sugere_regra && (
                <Badge borderRadius="full" colorScheme="purple" fontSize="9px" px={2}>
                  vira regra
                </Badge>
              )}
            </Flex>
            <Text fontSize="11px" color={C.subtle} mt="2px">
              {cluster.cnpj_cpf ? `${cluster.cnpj_cpf} · ` : ''}
              {fmtNum(cluster.qtd)} lançamentos · R$ {fmtValor(cluster.soma)}
            </Text>
          </Box>
          <Flex direction="column" align="flex-end" flexShrink={0} minW="92px">
            <Text fontSize="20px" fontWeight="700" color={C.ink} lineHeight="1">
              ×{cluster.qtd}
            </Text>
            <Box w="80px" h="5px" bg="rgba(139,92,246,0.14)" borderRadius="full" mt={1}>
              <Box w={`${barPct}%`} h="100%" bg={C.violet} borderRadius="full" />
            </Box>
          </Flex>
        </Flex>

        {cluster.memoria && (
          <Flex
            align="center"
            gap={2}
            mt={2.5}
            px={2.5}
            py={1.5}
            borderRadius="lg"
            bg="rgba(139,92,246,0.06)"
          >
            <Sparkle size={13} weight="fill" color={C.violet} />
            <Text fontSize="11px" color={C.muted} flex={1} minW={0} noOfLines={1}>
              Já aprendido: <b>{cluster.memoria.poc_codigo}</b> · pureza{' '}
              {Math.round(cluster.memoria.pureza * 100)}%
              {cluster.memoria.conflito ? ' · ⚠ conflito' : ''}
            </Text>
            <Button size="xs" variant="ghost" colorScheme="purple" onClick={usarMemoria}>
              usar essa
            </Button>
          </Flex>
        )}

        <Flex gap={2} mt={3} align="center" onClick={e => e.stopPropagation()}>
          <Box flex={1} minW={0}>
            <RSelect<PocSelectOption>
              size="sm"
              placeholder={`Conta para os ${cluster.qtd} (banco no ${isEntrada ? 'débito' : 'crédito'})…`}
              options={pocOptions}
              value={poc}
              onChange={opt => setPoc(opt as PocSelectOption | null)}
              isClearable
              components={{ Control: isEntrada ? POC_CONTROL_C : POC_CONTROL_D }}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              {...propsMenuSelect}
              styles={pocSelectStyles}
              chakraStyles={{ control: p => ({ ...p, borderRadius: 'lg', minHeight: '38px' }) }}
            />
          </Box>
          <Button
            size="sm"
            borderRadius="lg"
            px={4}
            color="white"
            fontWeight="600"
            bg={C.violet}
            _hover={{ filter: 'brightness(1.06)' }}
            isDisabled={!poc || travadoPorDispersao}
            isLoading={aplicando}
            loadingText="Aplicando"
            onClick={aplicar}
          >
            Aplicar aos {cluster.qtd}
          </Button>
        </Flex>

        {/* Sprint 2 — fecha o loop: o cluster vira regra permanente */}
        <Flex mt={2} justify="flex-end">
          <Button
            size="xs"
            variant="ghost"
            color={C.violet}
            fontWeight="600"
            isDisabled={!poc && !cluster.memoria}
            isLoading={criarRegra.isPending}
            loadingText="Criando regra"
            onClick={virarRegra}
            _hover={{ bg: 'rgba(139,92,246,0.08)' }}
          >
            + Virar regra (cobre próximos)
          </Button>
        </Flex>

        {travadoPorDispersao && (
          <Flex align="center" gap={1.5} mt={2} color={C.amber}>
            <Warning size={13} weight="fill" />
            <Text fontSize="11px">
              Valores espalhados (R$ {fmtValor(cluster.valor_min)}–{fmtValor(cluster.valor_max)}) —
              expanda e revise antes de aplicar ao grupo.
            </Text>
          </Flex>
        )}
      </Box>

      <Collapse in={open} animateOpacity>
        <Box
          bg="rgba(15,23,42,0.02)"
          px={3}
          py={2}
          borderTopWidth="1px"
          borderColor="rgba(15,23,42,0.06)"
        >
          {cluster.itens.map(item => (
            <ItemLinha key={item.id} lanc={item} />
          ))}
        </Box>
      </Collapse>
    </GlassCard>
  );
}

function OneOffRow({
  lanc,
  pocOptions,
  onApplied,
}: {
  lanc: IResiduoLancamento;
  pocOptions: PocSelectOption[];
  onApplied: () => void;
}) {
  const toast = useToast();
  const { mutateAsync, isPending } = useContaCilUpdateLancamento();
  const isEntrada = lanc.dc === 'C';
  const contraField: 'debito_poc' | 'credito_poc' = isEntrada ? 'credito_poc' : 'debito_poc';

  const salvar = async (valor: string | null) => {
    if (!valor) return;
    try {
      await mutateAsync({ id: lanc.id, [contraField]: valor });
      onApplied();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao classificar',
        description: extractApiErrorMessage(err, 'Tente novamente.'),
      });
    }
  };

  return (
    <Flex
      align="center"
      gap={2}
      py={2}
      px={1}
      borderBottomWidth="1px"
      borderColor="rgba(15,23,42,0.05)"
    >
      <Box flex={1} minW={0}>
        <Text fontSize="12px" color={C.ink} noOfLines={1}>
          {lanc.historico}
        </Text>
        <Text fontSize="10px" color={C.subtle}>
          {fmtData(lanc.data)} · {isEntrada ? '+' : '−'} {fmtValor(lanc.valor)}
        </Text>
      </Box>
      <Box w="240px" flexShrink={0} onClick={e => e.stopPropagation()}>
        <RSelect<PocSelectOption>
          size="sm"
          placeholder="Classificar…"
          options={pocOptions}
          isClearable
          isDisabled={isPending}
          components={{ Control: isEntrada ? POC_CONTROL_C : POC_CONTROL_D }}
          onChange={opt => salvar(opt ? (opt as PocSelectOption).value : null)}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          {...propsMenuSelect}
          styles={pocSelectStyles}
          chakraStyles={{ control: p => ({ ...p, borderRadius: 'lg', minHeight: '34px' }) }}
        />
      </Box>
    </Flex>
  );
}

const ONE_OFFS_VISIVEIS = 20;

export function ResiduoTab({ empresaId }: { empresaId: number }) {
  const { data, isFetching, isError } = useContaCilResiduoClusters(empresaId);
  const { data: pocs = [] } = useContaCilPocs(empresaId);
  const qc = useQueryClient();
  const pocOptions = React.useMemo<PocSelectOption[]>(
    () => pocs.map((p: IPocOption) => ({ value: p.codigo, label: `${p.codigo} · ${p.nome}` })),
    [pocs],
  );
  const onApplied = () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'school-ai'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
  };

  if (isFetching && !data) {
    return (
      <Flex py={10} justify="center" color={C.muted} gap={3} align="center">
        <Spinner size="sm" /> <Text fontSize="sm">Agrupando resíduo…</Text>
      </Flex>
    );
  }
  if (isError) {
    // Sem este ramo, erro de rede caía no estado vazio e renderizava a
    // mensagem comemorativa "tudo conciliado 🎉" — falso negativo.
    return (
      <GlassCard intensity="medium" p={10}>
        <Text fontSize="sm" color={C.ink} fontWeight="600" textAlign="center">
          Não foi possível carregar o resíduo.
        </Text>
        <Text fontSize="12px" color={C.muted} textAlign="center" mt={1}>
          Verifique a conexão e recarregue a página.
        </Text>
      </GlassCard>
    );
  }
  if (!data || data.total_lancamentos === 0) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Text fontSize="sm" color={C.muted} textAlign="center">
          A IA não deixou resíduo nesta empresa — tudo que ela analisou foi conciliado. 🎉
        </Text>
      </GlassCard>
    );
  }

  const maxQtd = data.clusters[0]?.qtd ?? 1;
  const oneOffsShown = data.one_offs.slice(0, ONE_OFFS_VISIVEIS);

  return (
    <Flex direction="column" gap={3}>
      <Text fontSize="12px" color={C.muted}>
        <b>{fmtNum(data.clusters.length)}</b> contrapartes recorrentes concentram a maior parte do
        resíduo — classifique uma e a IA aprende para todas. {isFetching && '· atualizando…'}
      </Text>

      {data.clusters.map(cluster => (
        <ClusterCard
          key={`${cluster.chave_norm}-${cluster.dc}`}
          cluster={cluster}
          pocOptions={pocOptions}
          maxQtd={maxQtd}
          onApplied={onApplied}
          empresaId={empresaId}
        />
      ))}

      {data.one_offs.length > 0 && (
        <GlassCard intensity="medium" p={4} mt={2}>
          <Text fontSize="13px" fontWeight="600" color={C.ink} mb={1}>
            Avulsos ({fmtNum(data.one_offs.length)})
          </Text>
          <Text fontSize="11px" color={C.muted} mb={2}>
            Sem padrão de contraparte (ex: Pix sem nome) — não viram regra; classifique caso a caso.
          </Text>
          {oneOffsShown.map(lanc => (
            <OneOffRow key={lanc.id} lanc={lanc} pocOptions={pocOptions} onApplied={onApplied} />
          ))}
          {data.one_offs.length > ONE_OFFS_VISIVEIS && (
            <Text fontSize="11px" color={C.subtle} textAlign="center" mt={2}>
              + {fmtNum(data.one_offs.length - ONE_OFFS_VISIVEIS)} avulsos — continue na tela
              Conciliar
            </Text>
          )}
        </GlassCard>
      )}
    </Flex>
  );
}
